import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { useBulkEditControl } from './useBulkEditControl';

export interface BulkEditControlProps {
  disabled?: boolean;
  compact?: boolean;
  onSave?: (changes: { [field: string]: any }) => void;
}

const compactIconButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  backgroundColor: 'background.paper',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

const compactPrimaryIconButtonSx = {
  ...compactIconButtonSx,
  borderColor: 'primary.main',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': {
    backgroundColor: 'primary.dark',
    borderColor: 'primary.dark',
  },
  '&.Mui-disabled': {
    borderColor: 'action.disabledBackground',
    backgroundColor: 'action.disabledBackground',
    color: 'action.disabled',
  },
};

export const BulkEditControl: React.FC<React.PropsWithChildren<BulkEditControlProps>> = ({
  disabled,
  compact = false,
  onSave,
}) => {
  const { bulkEditMode, setBulkEditMode, hasChanges, saveBulkEdits, undoBulkEdits } = useBulkEditControl(onSave);
  const bulkEditTooltip = disabled ? 'Select rows to start editing' : 'Bulk edit selected rows';
  const bulkEditHelperText = 'Bulk Edit';

  return bulkEditMode ? (
    compact ? (
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Cancel bulk edits">
          <span>
            <IconButton
              size="small"
              aria-label="Cancel bulk edits"
              disabled={!bulkEditMode}
              onClick={undoBulkEdits}
              data-cy="cancel-bulk-edits-button"
              sx={compactIconButtonSx}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Save bulk edits">
          <span>
            <IconButton
              size="small"
              aria-label="Save bulk edits"
              disabled={disabled || (bulkEditMode && !hasChanges)}
              onClick={saveBulkEdits}
              data-cy="save-bulk-edits-button"
              sx={compactPrimaryIconButtonSx}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    ) : (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          disabled={!bulkEditMode}
          onClick={undoBulkEdits}
          startIcon={<CloseIcon fontSize="small" />}
          variant="outlined"
          color="inherit"
          data-cy="cancel-bulk-edits-button"
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={disabled || (bulkEditMode && !hasChanges)}
          onClick={saveBulkEdits}
          startIcon={<CheckIcon fontSize="small" />}
          data-cy="save-bulk-edits-button"
        >
          Save
        </Button>
      </Stack>
    )
  ) : compact ? (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
      <Tooltip title={bulkEditTooltip}>
        <span>
          <IconButton
            size="small"
            aria-label="Bulk edit"
            disabled={disabled || (bulkEditMode && !hasChanges)}
            onClick={() => setBulkEditMode(true)}
            data-cy="bulk-edit-button"
            sx={compactIconButtonSx}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography
        variant="caption"
        color={disabled ? 'text.disabled' : 'text.secondary'}
        sx={{ whiteSpace: 'nowrap', lineHeight: 1.1, fontWeight: 600 }}
      >
        {bulkEditHelperText}
      </Typography>
    </Stack>
  ) : (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      disabled={disabled || (bulkEditMode && !hasChanges)}
      onClick={() => setBulkEditMode(true)}
      title={bulkEditTooltip}
      startIcon={<EditIcon fontSize="small" />}
      data-cy="bulk-edit-button"
      sx={{ whiteSpace: 'nowrap' }}
    >
      Bulk Edit
    </Button>
  );
};
